// Vuetify 3 plugin — CPA theme + defaults (doc 04 §10).
// WTA aesthetic: deep aubergine-purple brand on a bright mint-green wash; bright
// green for positive/rank-up, coral for rank-down; pill buttons, rounded cards.
import 'vuetify/styles'
import '@mdi/font/css/materialdesignicons.css'
import { createVuetify, type ThemeDefinition } from 'vuetify'
import { aliases, mdi } from 'vuetify/iconsets/mdi'

const cpaLight: ThemeDefinition = {
  dark: false,
  colors: {
    primary: '#2B0A40', // deep aubergine — brand, headings, buttons, active nav
    'primary-darken-1': '#1C0530', // hover / pressed / footer ink
    'primary-lighten-1': '#5B2F7E',
    secondary: '#00C46A', // bright accent green — avatars, highlights, up-states
    'secondary-darken-1': '#008246',
    accent: '#2EE88E',
    info: '#5B7CFA',
    success: '#00B368', // rank up / positive
    warning: '#F59E0B', // pending / waitlist
    error: '#E4482E', // rank down / negative
    surface: '#FFFFFF', // cards, tables
    'surface-bright': '#FFFFFF',
    'surface-variant': '#5D5470', // muted text token
    'on-surface-variant': '#FFFFFF',
    background: '#F3FBF6', // app canvas (mint-tinted white)
    cream: '#F4F1E8', // card footers
    'purple-2': '#3C1457', // lighter card purple
    neutral: '#6B7280',
    'on-primary': '#FFFFFF',
    'on-secondary': '#06231A',
    'on-success': '#FFFFFF',
    'on-error': '#FFFFFF',
    'on-cream': '#2B0A40',
    'on-purple-2': '#FFFFFF',
  },
  variables: {
    'border-color': '#2B0A40',
    'border-opacity': 0.1,
    'high-emphasis-opacity': 0.92,
    'medium-emphasis-opacity': 0.64,
    'theme-on-background': '#2B0A40',
  },
}

// First-class dark mode — premium aubergine night theme (also used by the
// courtside scoring console for high-glare outdoor readability).
const cpaDark: ThemeDefinition = {
  dark: true,
  colors: {
    primary: '#2EE88E', // brand accent flips to green for contrast on dark
    'primary-darken-1': '#15D67E',
    'primary-lighten-1': '#7CF3B6',
    secondary: '#2EE88E',
    'secondary-darken-1': '#15D67E',
    accent: '#2EE88E',
    info: '#8AA2FF',
    success: '#2EE88E',
    warning: '#FBBF24',
    error: '#FF6B6B',
    surface: '#1C0530', // cards
    'surface-bright': '#241038',
    'surface-variant': '#B3AAC4',
    'on-surface-variant': '#1C0530',
    background: '#120322',
    cream: '#241038',
    'purple-2': '#3C1457',
    neutral: '#9AA2B1',
    'on-primary': '#06231A',
    'on-secondary': '#06231A',
    'on-success': '#06231A',
    'on-error': '#1C0530',
    'on-cream': '#FFFFFF',
    'on-purple-2': '#FFFFFF',
  },
  variables: {
    'border-color': '#FFFFFF',
    'border-opacity': 0.12,
    'high-emphasis-opacity': 0.95,
    'medium-emphasis-opacity': 0.7,
  },
}

export const vuetify = createVuetify({
  icons: { defaultSet: 'mdi', aliases, sets: { mdi } },
  display: {
    mobileBreakpoint: 'md',
    thresholds: { xs: 0, sm: 600, md: 960, lg: 1280, xl: 1920, xxl: 2560 },
  },
  theme: {
    defaultTheme: 'cpaLight',
    themes: { cpaLight, cpaDark },
  },
  defaults: {
    VBtn: { rounded: 'pill', class: 'text-none font-weight-bold' },
    VCard: { rounded: 'xl', elevation: 0 },
    VChip: { rounded: 'pill' },
    // Consistent, smooth open/close for every dropdown, select & menu.
    VMenu: { transition: 'slide-y-transition' },
    VSelect: {
      variant: 'outlined',
      density: 'comfortable',
      rounded: 'lg',
      transition: 'slide-y-transition',
    },
    VTextField: { variant: 'outlined', density: 'comfortable', rounded: 'lg' },
    VTooltip: { transition: 'fade-transition' },
    VDialog: { transition: 'dialog-bottom-transition' },
  },
})

export default vuetify
